import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Upload, UserPlus, Filter, Edit2, Save, X, AlertTriangle, AlertOctagon, CheckCircle2, Circle, Link2, Trash2, Eraser, ArrowDownAZ, ArrowUpZA, QrCode, Search, ShieldCheck, HelpCircle, Mars, Venus, LogOut, Lock } from "lucide-react";
import { isFromAdminFestivals, clearAdminFestivalEntry } from "@/lib/adminNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import HelperPassQuickModal from "@/components/HelperPassQuickModal";
import { ClearAllModal, NoShowTodayModal, RemoveFromFestivalModal } from "@/pages/components/festivalplayers/FestivalPlayersModals";
import { loadHelperSession, clearHelperSession } from "@/pages/FestivalHelperRedeem";
import { useAuth } from "@/context/AuthContext";

// Iter316 — Colour bands are now driven by the festival's active
// `skill_ranges` array so preset switches (which change ids to things
// like `beginner`/`intermediate`) don't blank every chip on the page.
// The `LEGACY_BAND_STYLES` map is kept only so festivals that still run
// the pre-iter315 3-band default (green/blue/red) keep rendering while
// the dynamic map takes over for everything else.
const LEGACY_BAND_STYLES = {
  green: { bg: "#0F4B0F", fg: "#CCFF00", border: "#4CAF50" },
  blue: { bg: "#0F2A4B", fg: "#66C2FF", border: "#66C2FF" },
  red: { bg: "#4B0F0F", fg: "#FF4500", border: "#FF4500" },
  null: { bg: "#141414", fg: "#525252", border: "#262626" },
};

// Dim helper — the darker bg / brighter fg treatment we've always used
// on the roster chips, but computed from any hex the organiser picked.
const dimHex = (hex, alpha) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return "#141414";
  const h = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Turn the festival's skill_ranges into the { id → {bg,fg,border} }
// palette used by every chip / summary card. Legacy names are merged so
// existing 3-band festivals still render even if `skill_ranges` is empty.
function buildBandStyles(skillRanges) {
  const m = { ...LEGACY_BAND_STYLES };
  for (const r of skillRanges || []) {
    if (!r?.id || !r?.colour_hex) continue;
    m[String(r.id).toLowerCase()] = {
      bg: dimHex(r.colour_hex, 0.16),
      fg: r.colour_hex,
      border: r.colour_hex,
    };
  }
  return m;
}
function bandStyleFor(map, band) {
  return map[String(band ?? "null").toLowerCase()] || map.null || LEGACY_BAND_STYLES.null;
}

const STATUS_LABELS = {
  // Iter205 — friendlier labels; DB values unchanged. `imported` is
  // shown as "Awaiting Check-In" so admins never see the raw enum.
  imported: "Awaiting Check-In", arrived: "Checked-In", available: "Awaiting Court",
  assigned: "Next Up", on_court: "On Court", resting: "Resting",
  sin_bin: "Sin-Bin", inactive: "Inactive",
};

// Iter233 — deterministic ordering for the new "Band" + "Status" sort
// modes on the roster. Lower index = earlier in the list.
const BAND_ORDER = { green: 0, blue: 1, red: 2, spare: 3, unrated: 4, null: 5 };
const STATUS_ORDER = {
  on_court: 0, assigned: 1, available: 2, arrived: 3,
  resting: 4, sin_bin: 5, imported: 6, inactive: 7,
};

// Iter233 — A-Z jump-bar alphabet.
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// Backwards-compat: Iter230 stored "az"|"za"|"" here. Iter233 extends
// the vocabulary to include "booking"|"band"|"status".
const migrateSort = (raw) => {
  if (raw === "" || raw == null) return "";
  return ["az", "za", "booking", "band", "status"].includes(raw) ? raw : "az";
};

export default function FestivalPlayers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [summary, setSummary] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [bandFilter, setBandFilter] = useState("");
  // Iter230 / Iter233 — Roster toolbar state.
  //  · `sortMode`: az | za | booking | band | status | "" (off)
  //  · `searchQ`: fuzzy filter over name + booking id
  //  · `jumpBy`:  first | last — controls which name the A-Z jump bar targets
  //  · `qrHoverEnabled`: QR popup on name hover
  const [sortMode, setSortMode] = useState(() => {
    try { return migrateSort(window.localStorage.getItem(`rl_roster_sort_${id}`)) || "az"; } catch { return "az"; }
  });
  const [searchQ, setSearchQ] = useState("");
  const [jumpBy, setJumpBy] = useState(() => {
    try { return window.localStorage.getItem(`rl_roster_jump_${id}`) || "first"; } catch { return "first"; }
  });
  const [qrHoverEnabled, setQrHoverEnabled] = useState(() => {
    try { return window.localStorage.getItem(`rl_roster_qr_hover_${id}`) === "1"; } catch { return false; }
  });
  const [hoveredQr, setHoveredQr] = useState(null);
  const rowRefs = useRef({});
  const changeSort = (next) => {
    setSortMode(next);
    try { window.localStorage.setItem(`rl_roster_sort_${id}`, next); } catch {}
  };
  const changeJumpBy = (next) => {
    setJumpBy(next);
    try { window.localStorage.setItem(`rl_roster_jump_${id}`, next); } catch {}
  };
  const toggleQrHover = () => {
    const next = !qrHoverEnabled;
    setQrHoverEnabled(next);
    try { window.localStorage.setItem(`rl_roster_qr_hover_${id}`, next ? "1" : "0"); } catch {}
    if (!next) setHoveredQr(null);
  };
  const [festival, setFestival] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [editing, setEditing] = useState(null);
  // Iter205 — multi-select for bulk delete.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);
  // Iter234/235 — role gate. Owner + festival_only_helper get the full
  // editor. `check_in_helper` and `court_ops_helper` see the stripped
  // "names + check-in" view. Owners are the default until `/my-role`
  // returns so the UI never briefly locks the organiser out on first
  // paint.
  const [role, setRole] = useState({ is_owner: true, is_helper: false, role: "owner" });
  const canEditPlayers = role.role === "owner" || role.role === "festival_only_helper" || role.role === "deputy_helper" || (role.is_owner && !role.role);
  const isHelper = !canEditPlayers;
  // Iter461 — Post-festival read-only lock. When the festival's
  // `end_at` has passed, every mutation surface in Player Manager
  // is disabled for the organiser (and the editing helper roles).
  // Platform admins (Rally-Lab staff) retain full access so overrides
  // and fixes remain possible. Read-only actions (sort/search/export,
  // Template download, viewing data) remain enabled for everyone.
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "admin"
    || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");
  const festivalEnded = (() => {
    const end = festival?.end_at;
    if (!end) return false;
    try { return new Date(end).getTime() < Date.now(); } catch { return false; }
  })();
  const isReadOnly = festivalEnded && !isPlatformAdmin && canEditPlayers;
  // Iter449 · Fix 3 — Dedicated stripped-down view for the Check-In
  // helper role. ONLY the check_in_helper lands on this stripped
  // list; owners / festival_only / deputy / court_ops are
  // unaffected. The view shows just: player name (first/last sort
  // toggle), booking ID, QR code button, check-in button. Default
  // filter is `status === "imported"` (Awaiting Check-In) and
  // players disappear from the list the moment they're checked in
  // via an optimistic local filter (no refetch wait).
  const isCheckInOnly = role.role === "check_in_helper";
  const [checkInSort, setCheckInSort] = useState(() => {
    try {
      const v = window.localStorage.getItem(`rl_checkin_sort_${id}`);
      return v === "first" ? "first" : "last";
    } catch { return "last"; }
  });
  const changeCheckInSort = (next) => {
    setCheckInSort(next);
    try { window.localStorage.setItem(`rl_checkin_sort_${id}`, next); } catch { /* ignore */ }
  };
  const [qrModalPlayer, setQrModalPlayer] = useState(null);
  const [checkInBusyId, setCheckInBusyId] = useState(null);
  // Iter450 — Confirmation prompts for the Check-In desk. One for
  // the "shared the QR + booking ID with the player?" gate on
  // every check-in tap, one for the Sign out guard so a stray
  // thumb-tap doesn't nuke an active desk session.
  const [confirmCheckIn, setConfirmCheckIn] = useState(null);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  // Local optimistic hide — the players state refreshes on the
  // next poll via `load()`, but we remove the row immediately so
  // the desk sees a snappy, unambiguous "done" result.
  const [checkedInLocally, setCheckedInLocally] = useState(() => new Set());
  // Iter234 — Clear-All type-to-confirm modal. Guards against the
  // organiser mistaking the ✕ next to the search box for "Clear All".
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearBusy, setClearBusy] = useState(false);
  // Iter235b — quick-generate helper pass modal (owner + festival_only_helper only).
  const [showHelperQuick, setShowHelperQuick] = useState(false);
  // Iter316 B3 — Player cap status ({count, cap, locked, pre_purchase,
  // pass_type}). When `locked` is true (or the active license has hit
  // its cap even once), Add-Player + Import are permanently disabled
  // for the event day. Deleting players does NOT re-open a slot.
  const [capStatus, setCapStatus] = useState(null);
  const capLocked = !!(capStatus && capStatus.locked);

  // Iter316 — Dynamic band → hex-style lookup. Rebuilt whenever the
  // festival's skill_ranges change so preset switches recolour every
  // chip immediately (root-cause fix for the "colours vanish after
  // loading a preset" bug: hardcoded map only knew green/blue/red).
  const bandStyles = useMemo(
    () => buildBandStyles(festival?.skill_ranges),
    [festival?.skill_ranges],
  );
  // Iter326 · Bug 4 — Single-source-of-truth for the QR gate. Every
  // QR affordance (hover reveal, toggle button, copy-link icon) reads
  // this flag so a stale `qrHoverEnabled=true` in localStorage can't
  // leak a QR when the festival hasn't purchased Self-Service.
  const selfServiceOn = festival?.license?.self_service_enabled !== false;
  // Iter331 · Bug 12 — Per-player delete confirmation. Shows a modal
  // warning the organiser that the imported player-cap SLOT is not
  // recovered on delete (cap high-water mark is retained). Prevents
  // accidental deletes that surprise the organiser at upgrade time.
  const [deleteConfirmFor, setDeleteConfirmFor] = useState(null);
  // Iter392 — Import result summary. `null` = no fresh import; when
  // populated we show a banner with `X imported with gender data, Y
  // flagged` + a bulk-assign UI over the flagged rows.
  const [importSummary, setImportSummary] = useState(null);
  const [flaggedSelected, setFlaggedSelected] = useState(() => new Set());

  const load = async () => {
    const q = new URLSearchParams();
    if (statusFilter) q.set("status", statusFilter);
    if (bandFilter) q.set("band", bandFilter);
    try {
      const [p, f, r, cs] = await Promise.all([
        api.get(`/festival/${id}/players${q.toString() ? `?${q}` : ""}`),
        api.get(`/festival/${id}`),
        api.get(`/festival/${id}/my-role`).catch(() => ({ data: { is_owner: true, is_helper: false } })),
        api.get(`/festival/${id}/players/cap-status`).catch(() => ({ data: null })),
      ]);
      setPlayers(p.data.items || []);
      setSummary(p.data.summary || {});
      setFestival(f.data);
      setRole(r.data || { is_owner: true, is_helper: false });
      setCapStatus(cs.data || null);
    } catch (e) {
      // Iter446 — Helper-PIN single-device kickouts are handled by
      // the api.js interceptor (amber toast + hard redirect). Here
      // we simply swallow the error so no page-level banner
      // flashes while the redirect lands. All other errors keep
      // their existing behaviour (upstream `useEffect` doesn't
      // render a top-level error strip for this screen, so no
      // additional UI change is needed).
      if (e?.__helperKickout) return;
      throw e;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, statusFilter, bandFilter]);

  // Iter447 — Check-In helper kickout polling. Fires IMMEDIATELY on
  // mount (no 10 s delay) and every 5 s afterwards — exactly the
  // same cadence OPS Assist uses, so Check-In behaves identically.
  // We ping a single lightweight endpoint (`/my-role`) rather than
  // the four-call `load()` to keep polling cheap. The ping's only
  // job is to let the backend 401 a stale helper JWT; the api.js
  // interceptor then wipes the token, fires the amber toast, and
  // hard-redirects to the redeem screen. Visibility / focus /
  // pageshow all retrigger the ping so returning to a backgrounded
  // tab kicks the stale device immediately.
  useEffect(() => {
    if (!isHelper) return undefined;
    let alive = true;
    const ping = () => {
      if (!alive) return;
      if (typeof document !== "undefined" && document.hidden) return;
      // Fire-and-forget; errors are handled by the api.js
      // interceptor (kickout → toast + redirect) or silently
      // ignored (transient network blip — the next tick retries).
      api.get(`/festival/${id}/my-role`).catch(() => { /* handled by interceptor */ });
    };
    ping();
    const iv = setInterval(ping, 5000);
    const onVis = () => ping();
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [isHelper, id]);

  // Iter446/447 — Defence-in-depth: if the helper session has been
  // wiped from localStorage (either by the api.js kickout
  // interceptor or by the helper tapping Sign Out), bounce the
  // Check-In helper off this page immediately. Owners / Rally-Lab
  // users have no `rl_festival_helper_token` entry by design, so
  // this only fires when a helper session was present and has just
  // been invalidated.
  useEffect(() => {
    if (!isHelper) return undefined;
    const checkSession = () => {
      if (loadHelperSession()) return;
      if (typeof window === "undefined") return;
      // Match the api.js interceptor's redirect target so both
      // code paths land on the same screen (the redeem URL when
      // we still know the pass_id, otherwise the festival home).
      let target = id ? `/festival/${id}` : "/login";
      try {
        const raw = localStorage.getItem("rl_festival_helper_token");
        if (!raw && typeof window !== "undefined") {
          const m = window.location.pathname.match(/\/festival\/([^/]+)/);
          if (m) target = `/festival/${m[1]}`;
        }
      } catch { /* ignore */ }
      if (window.location.pathname !== target) {
        try { window.location.replace(target); }
        catch { try { window.location.href = target; } catch { /* ignore */ } }
      }
    };
    checkSession();
    const iv = setInterval(checkSession, 1000);
    const onVis = () => checkSession();
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      clearInterval(iv);
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [isHelper, id]);
  // Iter332 · Bug 18 — Helper back-button guard for Player Manager
  // when opened as a Check-In helper session. Pushes a duplicate
  // history state so pressing browser Back stays inside the festival
  // instead of dropping to /login (previous history entry from the
  // QR redemption URL).
  useEffect(() => {
    if (!isHelper) return;
    const guard = () => {
      try { window.history.pushState(null, "", window.location.href); } catch {}
    };
    guard();
    window.addEventListener("popstate", guard);
    return () => window.removeEventListener("popstate", guard);
  }, [isHelper]);

  const onPickFile = () => fileRef.current?.click();

  const doPreview = async (file) => {
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/festival/${id}/players/import-preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview({ ...data, _file: file });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import preview failed");
    } finally { setImportBusy(false); }
  };

  const doImport = async () => {
    if (!importPreview?._file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importPreview._file);
      const { data } = await api.post(`/festival/${id}/players/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} · skipped ${data.skipped_duplicates}`);
      setImportPreview(null);
      // Iter392 — surface gender summary so OPS can bulk-assign the
      // flagged rows without leaving the roster page.
      if (typeof data.gender_ok === "number") {
        setImportSummary({
          gender_ok: data.gender_ok,
          gender_flagged: data.gender_flagged || [],
        });
        setFlaggedSelected(new Set());
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImportBusy(false); }
  };

  const saveManual = async (form) => {
    try {
      await api.post(`/festival/${id}/players`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        imported_rating: form.rating ? Number(form.rating) : null,
        booking_id: form.booking_id.trim() || null,
        source: form.source || "manual",
        notes: form.notes.trim() || null,
      });
      toast.success("Player added");
      setShowManual(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const saveEdit = async (pid, patch) => {
    try {
      await api.patch(`/festival/${id}/players/${pid}`, patch);
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const overrideRating = async (p) => {
    const val = window.prompt("New operational rating (0–8):", String(p.current_rating ?? ""));
    if (val === null) return;
    const parsed = Number(val);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 8) return toast.error("Rating must be 0–8");
    const reason = window.prompt("Reason for override?");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/festival/${id}/players/${p.id}/rating-override`, {
        current_rating: parsed, reason: reason.trim(),
      });
      toast.success("Rating updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Iter205 — bulk delete helpers.
  const toggleSelect = (pid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) => {
    if (prev.size === players.length) return new Set();
    return new Set(players.map((p) => p.id));
  });
  const deleteSelected = async () => {
    if (selected.size === 0 || bulkBusy) return;
    if (!window.confirm(`Delete ${selected.size} player${selected.size === 1 ? "" : "s"} from this festival?`)) return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    let ok = 0; let fail = 0;
    for (const pid of ids) {
      try { await api.delete(`/festival/${id}/players/${pid}`); ok += 1; }
      catch { fail += 1; }
    }
    setSelected(new Set());
    setBulkBusy(false);
    if (fail === 0) toast.success(`Deleted ${ok} player${ok === 1 ? "" : "s"}`);
    else toast.warning(`Deleted ${ok} · ${fail} failed`);
    await load();
  };

  // Iter206 — Bulk check-in. Advance every selected `imported` row to
  // `arrived` (Checked-In). Rows already past Awaiting-Check-In are
  // silently skipped so the click is idempotent.
  const checkInSelected = async () => {
    if (selected.size === 0 || bulkBusy) return;
    const targets = players.filter((p) => selected.has(p.id) && p.status === "imported");
    if (targets.length === 0) {
      toast.info("Nothing to check in — those players are already checked in");
      return;
    }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const p of targets) {
      try { await api.patch(`/festival/${id}/players/${p.id}`, { status: "arrived" }); ok += 1; }
      catch { fail += 1; }
    }
    setSelected(new Set());
    setBulkBusy(false);
    if (fail === 0) toast.success(`Checked in ${ok} player${ok === 1 ? "" : "s"}`);
    else toast.warning(`Checked in ${ok} · ${fail} failed`);
    await load();
  };

  // Iter234 — one-tap check-in for helpers (per-row).
  const quickCheckIn = async (p) => {
    try {
      await api.patch(`/festival/${id}/players/${p.id}`, { status: "arrived" });
      toast.success(`${p.first_name} ${p.last_name} checked in`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    }
  };

  // Iter381 · Fix 3 — Bulk status setter. Applies the same status
  // patch to every selected player. Reuses the per-row PATCH
  // endpoint (already permission-checked) instead of adding a new
  // bulk endpoint. Skips players already in the target status so
  // the toast count reflects only real changes.
  const bulkStatus = async (status, label) => {
    if (selected.size === 0 || bulkBusy) return;
    const targets = players.filter((p) => selected.has(p.id) && p.status !== status);
    if (targets.length === 0) {
      toast.info(`Nothing to update — every selected player is already ${label}`);
      return;
    }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const p of targets) {
      try { await api.patch(`/festival/${id}/players/${p.id}`, { status }); ok += 1; }
      catch { fail += 1; }
    }
    setSelected(new Set());
    setBulkBusy(false);
    if (fail === 0) toast.success(`${label}: ${ok} player${ok === 1 ? "" : "s"}`);
    else toast.warning(`${label}: ${ok} · ${fail} failed`);
    await load();
  };

  // Iter392 — Bulk gender setter. Used by both the roster's normal
  // multi-select bar (any player) AND the post-import flagged summary
  // (subset of freshly-imported rows). `ids` explicit so the caller
  // decides which set is in scope.
  const bulkGender = async (ids, gender) => {
    if (!ids || ids.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/festival/${id}/players/bulk-gender`, {
        player_ids: ids, gender,
      });
      toast.success(`Set ${gender === "male" ? "Male" : "Female"} on ${r.data.updated} player${r.data.updated === 1 ? "" : "s"}`);
      // If those ids overlap the roster bulk selection, clear it.
      setSelected((prev) => {
        const next = new Set(prev);
        for (const pid of ids) next.delete(pid);
        return next;
      });
      // Same for the flagged-panel selection.
      setFlaggedSelected((prev) => {
        const next = new Set(prev);
        for (const pid of ids) next.delete(pid);
        return next;
      });
      // Drop the imported-summary rows that are now resolved so the
      // banner shrinks in real time. If everything's resolved, hide.
      setImportSummary((prev) => {
        if (!prev) return prev;
        const idset = new Set(ids);
        const remaining = (prev.gender_flagged || []).filter((p) => !idset.has(p.id));
        if (remaining.length === 0) return null;
        return { ...prev, gender_flagged: remaining, gender_ok: prev.gender_ok + (prev.gender_flagged.length - remaining.length) };
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk gender update failed");
    } finally {
      setBulkBusy(false);
    }
  };
  const doClearAll = async () => {
    setClearBusy(true);
    try {
      const r = await api.delete(`/festival/${id}/players?confirm=yes`);
      const snapId = r.data.undo_snapshot_id;
      setShowClearModal(false);
      setClearConfirmText("");
      await load();
      toast.success(`Removed ${r.data.deleted} player${r.data.deleted === 1 ? "" : "s"}`, {
        duration: 10000,
        action: snapId ? {
          label: "Undo",
          onClick: async () => {
            try {
              const u = await api.post(`/festival/${id}/players/undo-clear`, { snapshot_id: snapId });
              toast.success(`Restored ${u.data.players_restored} player${u.data.players_restored === 1 ? "" : "s"}`);
              await load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Undo window expired");
            }
          },
        } : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setClearBusy(false);
    }
  };

  // Iter318 — No-show today + permanent remove. Two-step confirmation
  // modals so the organiser can't accidentally nuke a player mid-event.
  const [noShowTarget, setNoShowTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeConfirmText, setRemoveConfirmText] = useState("");
  const [rowActionBusy, setRowActionBusy] = useState(false);

  const doNoShowToday = async () => {
    if (!noShowTarget) return;
    setRowActionBusy(true);
    try {
      await api.post(`/festival/${id}/players/${noShowTarget.id}/no-show-today`);
      toast.success(`${noShowTarget.first_name} ${noShowTarget.last_name} marked no-show for today`);
      setNoShowTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark no-show");
    } finally { setRowActionBusy(false); }
  };

  const doRemoveFromFestival = async () => {
    if (!removeTarget) return;
    if (removeConfirmText.trim().toLowerCase() !== "remove") {
      toast.error("Type REMOVE to confirm");
      return;
    }
    setRowActionBusy(true);
    try {
      await api.post(`/festival/${id}/players/${removeTarget.id}/remove`);
      toast.success(`${removeTarget.first_name} ${removeTarget.last_name} removed from festival`);
      setRemoveTarget(null);
      setRemoveConfirmText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally { setRowActionBusy(false); }
  };

  if (!festival) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;

  // Iter449 · Fix 3 — Stripped Check-In helper view. Short-circuits
  // the full Player-Manager render below so the Check-In helper
  // sees ONLY: name + booking + QR + check-in button, filtered to
  // Awaiting Check-In. Everything else (status filters, bands,
  // search, source column, rating, ratings override, QR hover
  // toolbar, delete / remove / no-show buttons) stays available
  // for owners + the editing helper roles.
  if (isCheckInOnly) {
    const awaiting = (players || [])
      .filter((p) => p.status === "imported")
      .filter((p) => !checkedInLocally.has(p.id))
      .filter((p) => !p.removed_from_festival)
      .sort((a, b) => {
        const [ak, bk] = checkInSort === "first"
          ? [`${a.first_name || ""} ${a.last_name || ""}`, `${b.first_name || ""} ${b.last_name || ""}`]
          : [`${a.last_name || ""} ${a.first_name || ""}`, `${b.last_name || ""} ${b.first_name || ""}`];
        return ak.localeCompare(bk, undefined, { sensitivity: "base" });
      });
    const doSignOut = () => {
      try { clearHelperSession(); } catch { /* ignore */ }
      try { window.location.replace(`/festival/${id}`); }
      catch { try { window.location.href = `/festival/${id}`; } catch { /* ignore */ } }
    };
    const doCheckIn = async (p) => {
      if (checkInBusyId) return;
      setCheckInBusyId(p.id);
      // Optimistic hide — the row vanishes the instant the helper
      // taps the button. If the PATCH fails we re-surface it and
      // toast the backend reason so the desk can retry.
      setCheckedInLocally((prev) => {
        const next = new Set(prev);
        next.add(p.id);
        return next;
      });
      try {
        await api.patch(`/festival/${id}/players/${p.id}`, { status: "arrived" });
        toast.success(`${p.first_name} ${p.last_name} checked in`);
      } catch (e) {
        if (e?.__helperKickout) return;
        setCheckedInLocally((prev) => {
          const next = new Set(prev);
          next.delete(p.id);
          return next;
        });
        toast.error(e?.response?.data?.detail || "Check-in failed");
      } finally {
        setCheckInBusyId(null);
      }
    };
    const qrUrl = qrModalPlayer && selfServiceOn && qrModalPlayer.self_service_token
      ? `${window.location.origin}/festival/${id}/me/${qrModalPlayer.self_service_token}`
      : "";
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="festival-players-checkin">
        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* Header — minimal: badge + sign-out */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-mono text-[#FFB020] text-[10px]" data-testid="checkin-mode-badge">CHECK-IN DESK</div>
              <h1 className="h-display text-2xl mt-0.5">{festival.name}</h1>
              <div className="text-[11px] text-[#A3A3A3] mt-1" data-testid="checkin-awaiting-count">
                {awaiting.length} awaiting check-in
              </div>
            </div>
            <button
              type="button"
              onClick={() => setConfirmSignOut(true)}
              data-testid="checkin-signout"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white"
              title="End helper session"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>

          {/* First / Last sort toggle — same pattern as OPS Assist */}
          <div className="flex items-center gap-2 mb-4" role="group" aria-label="Sort by name">
            <span className="text-[10px] uppercase tracking-widest text-[#525252]">Sort</span>
            <button
              type="button"
              onClick={() => changeCheckInSort("first")}
              data-testid="checkin-sort-first"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border ${checkInSort === "first" ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}
            >
              First name
            </button>
            <button
              type="button"
              onClick={() => changeCheckInSort("last")}
              data-testid="checkin-sort-last"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border ${checkInSort === "last" ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}
            >
              Last name
            </button>
          </div>

          {/* Awaiting list */}
          {awaiting.length === 0 ? (
            <div className="card-surface p-8 text-center" data-testid="checkin-empty">
              <CheckCircle2 className="w-10 h-10 text-[#4CAF50] mx-auto mb-3" />
              <div className="h-display text-xl">Everyone's checked in</div>
              <p className="text-sm text-[#A3A3A3] mt-1">New arrivals will appear here as they're added to the roster.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#141414] border border-[#262626] bg-[#0F0F0F]" data-testid="checkin-list">
              {awaiting.map((p) => (
                <li
                  key={p.id}
                  data-testid={`checkin-row-${p.id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-medium truncate" data-testid={`checkin-name-${p.id}`}>
                      {checkInSort === "last"
                        ? `${p.last_name || ""}${p.first_name ? `, ${p.first_name}` : ""}`
                        : `${p.first_name || ""} ${p.last_name || ""}`.trim()}
                    </div>
                    {/* Iter450 — Booking ID lifted to a bold, high-
                        contrast chip so the desk can scan it in a
                        crowded room without squinting. Yellow-amber
                        on near-black keeps WCAG AAA at this size on
                        a bright phone screen in daylight. */}
                    {p.booking_id ? (
                      <div
                        className="mt-1 inline-block px-2 py-0.5 text-sm font-extrabold tracking-wide text-[#FFB020] bg-[#1A140A] border border-[#FFB020]/50 font-mono"
                        data-testid={`checkin-booking-${p.id}`}
                      >
                        {p.booking_id}
                      </div>
                    ) : (
                      <div
                        className="mt-1 text-[11px] uppercase tracking-wider text-[#525252] italic"
                        data-testid={`checkin-booking-${p.id}`}
                      >
                        No booking ID
                      </div>
                    )}
                  </div>
                  {selfServiceOn && p.self_service_token && (
                    <button
                      type="button"
                      onClick={() => setQrModalPlayer(p)}
                      data-testid={`checkin-qr-${p.id}`}
                      className="shrink-0 inline-flex items-center justify-center w-10 h-10 border border-[#262626] text-[#A3A3A3] hover:text-[#CCFF00] hover:border-[#CCFF00]"
                      title="Show self-service QR for the player"
                      aria-label={`Show QR code for ${p.first_name} ${p.last_name}`}
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={checkInBusyId === p.id}
                    onClick={() => setConfirmCheckIn(p)}
                    data-testid={`checkin-confirm-${p.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-40 disabled:cursor-wait"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {checkInBusyId === p.id ? "…" : "Check in"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* QR modal — tap backdrop or close to dismiss */}
        {qrModalPlayer && (
          <div
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 p-4"
            onClick={() => setQrModalPlayer(null)}
            data-testid="checkin-qr-modal"
          >
            <div
              className="w-full max-w-xs bg-[#0A0A0A] border-2 border-[#CCFF00] p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="label-mono text-[#CCFF00] text-[10px]">SELF-SERVICE QR</div>
                <button
                  type="button"
                  onClick={() => setQrModalPlayer(null)}
                  data-testid="checkin-qr-close"
                  className="text-[#A3A3A3] hover:text-white"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-white p-3">
                {qrUrl ? (
                  <QRCodeSVG value={qrUrl} size={320} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: "100%", height: "auto", imageRendering: "pixelated" }} />
                ) : (
                  <div className="text-xs text-black p-6 text-center">QR not available for this player.</div>
                )}
              </div>
              <div className="text-[11px] text-white mt-3 text-center font-medium" data-testid="checkin-qr-name">
                {qrModalPlayer.first_name} {qrModalPlayer.last_name}
              </div>
              {qrModalPlayer.booking_id && (
                <div className="text-[10px] text-[#A3A3A3] text-center mt-0.5">
                  Booking {qrModalPlayer.booking_id}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Iter450 — Check-In confirmation. Reminds the helper to
            hand the player their QR + Booking ID BEFORE the status
            flips, so the player always leaves the desk with a way
            to self-serve from their own phone. Yes → runs the
            optimistic PATCH (via `doCheckIn`). No → closes the
            prompt, row stays untouched. */}
        {confirmCheckIn && (
          <div
            className="fixed inset-0 z-[96] flex items-center justify-center bg-black/85 p-4"
            onClick={() => setConfirmCheckIn(null)}
            data-testid="checkin-confirm-modal"
          >
            <div
              className="w-full max-w-sm bg-[#0A0A0A] border-2 border-[#CCFF00] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="label-mono text-[#CCFF00] text-[10px] mb-2">BEFORE YOU CHECK IN</div>
              <h2 className="h-display text-xl leading-snug mb-2" data-testid="checkin-confirm-title">
                Have you shared the QR code and booking ID with this player?
              </h2>
              <div className="text-[11px] text-[#A3A3A3] mb-5" data-testid="checkin-confirm-player">
                {confirmCheckIn.first_name} {confirmCheckIn.last_name}
                {confirmCheckIn.booking_id ? ` · ${confirmCheckIn.booking_id}` : ""}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmCheckIn(null)}
                  data-testid="checkin-confirm-no"
                  className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const target = confirmCheckIn;
                    setConfirmCheckIn(null);
                    doCheckIn(target);
                  }}
                  data-testid="checkin-confirm-yes"
                  className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] bg-[#CCFF00] text-black hover:bg-white hover:border-white"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Iter450 — Sign-out confirmation. Prevents an accidental
            tap on a tablet mounted at the desk from ending the
            session mid-shift. Confirm fires the Iter449 `doSignOut`
            flow (wipe helper token + hard redirect). */}
        {confirmSignOut && (
          <div
            className="fixed inset-0 z-[96] flex items-center justify-center bg-black/85 p-4"
            onClick={() => setConfirmSignOut(false)}
            data-testid="checkin-signout-modal"
          >
            <div
              className="w-full max-w-sm bg-[#0A0A0A] border-2 border-[#FFB020] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="label-mono text-[#FFB020] text-[10px] mb-2">END SESSION</div>
              <h2 className="h-display text-xl leading-snug mb-5" data-testid="checkin-signout-title">
                Are you sure you are done?
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmSignOut(false)}
                  data-testid="checkin-signout-cancel"
                  className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmSignOut(false); doSignOut(); }}
                  data-testid="checkin-signout-confirm"
                  className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest font-bold border border-[#FFB020] bg-[#FFB020] text-black hover:bg-white hover:border-white"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-players">
      {/* Iter461 — Post-festival read-only banner. Organiser +
          editing-helper roles see this once `festival.end_at` has
          passed; platform admins never see it (unrestricted access
          preserved). "Request Festival Extension" is a stub for now
          — spec specifies no backend wiring in this iteration. */}
      {isReadOnly && (
        <div
          data-testid="festival-ended-banner"
          className="border border-[#FFB020] bg-[#1A140A] p-3 mb-4 flex items-start gap-3 flex-wrap"
        >
          <Lock className="w-5 h-5 text-[#FFB020] shrink-0 mt-0.5" />
          <div className="min-w-0 text-sm text-white flex-1">
            <div className="label-mono text-[#FFB020] text-[10px]">FESTIVAL ENDED</div>
            This festival has ended. Player data is now read-only.
          </div>
          <button
            type="button"
            data-testid="festival-ended-request-extension"
            onClick={() => { /* stub — backend wiring deferred */ }}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black"
          >
            Request Festival Extension
          </button>
        </div>
      )}
      {/* Iter326 · Bug 3 — Helper scoped Check-In view has no upward
          navigation. Owners + full-editor roles still see the crumb. */}
      {!isHelper && (
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {isFromAdminFestivals() && (
            <button
              type="button"
              onClick={() => { clearAdminFestivalEntry(); navigate("/admin/festivals"); }}
              data-testid="back-to-festival-manager"
              className="text-[11px] uppercase tracking-wider text-[#CCFF00] hover:text-white inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Festival Manager
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/festival/${id}`)}
            data-testid="festival-players-back"
            className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {festival.name}
          </button>
        </div>
      )}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="label-mono text-[#CCFF00] mb-1">FESTIVAL · PLAYER MANAGER</div>
          <h1 className="h-display text-3xl">Players</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">
            {summary.total || 0} in roster · Green ≤ 3.0 · Blue = 3.5 · Red ≥ 4.0
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Iter233 — Sort mode dropdown (replaces the old A/Z cycle). */}
          <label className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3]" title="Sort the roster by name, booking, band, or status">
            {sortMode === "za" ? <ArrowUpZA className="w-4 h-4" /> : <ArrowDownAZ className="w-4 h-4" />}
            <span>Sort:</span>
            <select
              value={sortMode}
              onChange={(e) => changeSort(e.target.value)}
              data-testid="players-sort-select"
              data-sort={sortMode || "none"}
              className="bg-transparent focus:outline-none uppercase tracking-wider text-xs"
            >
              <option value="az">Name A → Z</option>
              <option value="za">Name Z → A</option>
              <option value="booking">Booking ID</option>
              <option value="band">Band (G→B→R)</option>
              <option value="status">Status</option>
              <option value="">Off (import order)</option>
            </select>
          </label>
          {/* Iter233 — Search box (name or booking ID). */}
          <div className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#262626] focus-within:border-[#CCFF00]" title="Type any part of a name or booking ID">
            <Search className="w-4 h-4 text-[#525252]" />
            <input
              type="search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search name or booking…"
              data-testid="players-search"
              className="bg-transparent focus:outline-none w-40 sm:w-56 text-white placeholder:text-[#525252]"
            />
            {searchQ && (
              <button
                type="button"
                onClick={() => setSearchQ("")}
                data-testid="players-search-clear"
                className="text-[#525252] hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={toggleQrHover}
            data-testid="players-qr-hover-toggle"
            hidden={!selfServiceOn}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border ${qrHoverEnabled ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}
            title="Hover a player's name to reveal their self-service QR — perfect for check-in helpers"
          >
            <QrCode className="w-4 h-4" /> QR preview {qrHoverEnabled ? "on" : "off"}
          </button>
          {!isHelper && (<>
          <button
            type="button"
            onClick={() => setShowHelperQuick(true)}
            disabled={isReadOnly}
            data-testid="players-helper-quick-cta"
            title={isReadOnly ? "Festival ended — read-only" : "Generate a QR + code pass to onboard a helper on the fly"}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#66C2FF]"
          >
            <ShieldCheck className="w-4 h-4" /> Add helper
          </button>
          <Link
            to={`/festival/${id}/live`}
            data-testid="players-live-ops-nav"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
            title="Jump to Live Ops for this festival"
          >
            <ArrowLeft className="w-4 h-4 rotate-180" /> Go to Live Ops
          </Link>
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/festival/players/template.xlsx`}
            download
            data-testid="players-download-template"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]"
            title="Download the Excel template with example rows"
          >
            <Upload className="w-4 h-4 rotate-180" /> Template
          </a>
          <button
            type="button"
            onClick={onPickFile}
            disabled={capLocked || isReadOnly}
            data-testid="players-import-cta"
            title={isReadOnly ? "Festival ended — read-only" : (capLocked ? "Player cap reached — upgrade your tier to import more" : undefined)}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#66C2FF]"
          >
            <Upload className="w-4 h-4" /> Import CSV / XLSX
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="players-import-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) doPreview(f); e.target.value = ""; }}
          />
          <button
            type="button"
            onClick={() => { setClearConfirmText(""); setShowClearModal(true); }}
            data-testid="players-clear-all"
            disabled={!summary.total || isReadOnly}
            title={isReadOnly ? "Festival ended — read-only" : "Delete every player + reset every court"}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Eraser className="w-4 h-4" /> Clear All
          </button>
          <button
            type="button"
            onClick={() => setShowManual(true)}
            disabled={capLocked || isReadOnly}
            data-testid="players-manual-add-cta"
            title={isReadOnly ? "Festival ended — read-only" : (capLocked ? "Player cap reached — upgrade your tier to add more" : undefined)}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            <UserPlus className="w-4 h-4" /> Manual add
          </button>
          </>)}
          {isHelper && (
            <span
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#FFB020] text-[#FFB020]"
              data-testid="players-helper-mode-badge"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Desk mode · check-in only
            </span>
          )}
        </div>
      </div>

      {/* Iter316 B3 — Player cap banner. Shown once the tier's player_cap
          has ever been reached, or as a soft warning when pre-purchase
          count exceeds the recommended tier. Deleting players does NOT
          reset the frozen state — that's the Iter316 spec. */}
      {capStatus && capStatus.locked && (
        <div
          className="border border-[#FFB020] bg-[#1A140A] p-3 mb-4 flex items-start gap-2"
          data-testid="players-cap-locked-banner"
        >
          <AlertOctagon className="w-5 h-5 text-[#FFB020] shrink-0 mt-0.5" />
          <div className="min-w-0 text-sm text-white">
            <div className="label-mono text-[#FFB020] text-[10px]">PLAYER CAP REACHED</div>
            You're at {capStatus.count}
            {capStatus.cap != null ? ` / ${capStatus.cap}` : ""} players. Add Player and Import are
            now disabled for this event day — deleting a player will not free up a slot. Upgrade
            your tier or contact us for a bespoke package to invite more.
            <div className="mt-2 text-[10px] text-[#66C2FF]" data-testid="players-cap-self-service-hint">
              💡 Enable Player Self-Service to tie check-ins to verified player accounts and
              prevent unauthorised substitutions.{" "}
              <Link to={`/festival/${id}#addons`} className="underline hover:text-white">
                Add Self-Service →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Status buckets */}
      <div className="card-surface p-3 mb-4 flex gap-2 overflow-x-auto" data-testid="players-status-bar">
        <button
          type="button"
          onClick={() => setStatusFilter("")}
          data-testid="players-filter-all"
          className={`whitespace-nowrap px-3 py-1.5 text-[10px] uppercase tracking-wider border ${!statusFilter ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}
        >
          All · {summary.total || 0}
        </button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatusFilter(key)}
            data-testid={`players-filter-${key}`}
            className={`whitespace-nowrap px-3 py-1.5 text-[10px] uppercase tracking-wider border ${statusFilter === key ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}
          >
            {label} · {summary[key] || 0}
          </button>
        ))}
      </div>

      {/* Band filters */}
      <div className="flex gap-2 mb-4 items-center">
        <span className="text-[10px] uppercase tracking-wider text-[#525252] flex items-center gap-1">
          <Filter className="w-3 h-3" /> Band:
        </span>
        {["", "green", "blue", "red"].map((b) => (
          <button
            key={b || "any"}
            type="button"
            onClick={() => setBandFilter(b)}
            data-testid={`players-band-${b || "any"}`}
            className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${bandFilter === b ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3]"}`}
          >
            {b || "Any"}
          </button>
        ))}
      </div>

      {players.length === 0 ? (
        <div className="card-surface p-8 text-center" data-testid="players-empty">
          <UserPlus className="w-10 h-10 text-[#CCFF00] mx-auto mb-3" />
          <div className="h-display text-xl">No players yet</div>
          <p className="text-sm text-[#A3A3A3] mt-1">Import your booking export or add players one by one.</p>
        </div>
      ) : (
        <div className="card-surface overflow-x-auto" data-testid="players-table">
          {/* Iter392 — Post-import gender summary + inline bulk assign.
               X players imported with gender data, Y flagged — gender
               unknown. Flagged rows can be multi-selected and bulk-
               assigned Male / Female in a single action. */}
          {!isHelper && importSummary && (
            <div className="p-4 border-b border-[#262626] bg-[#141414]" data-testid="players-import-gender-summary">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="text-[#CCFF00] uppercase tracking-widest font-bold">Import complete</span>
                <span className="text-[#A3A3A3]" data-testid="import-gender-ok-count">
                  {importSummary.gender_ok} player{importSummary.gender_ok === 1 ? "" : "s"} imported with gender data
                </span>
                <span className="text-[#FFB020]" data-testid="import-gender-flagged-count">
                  {importSummary.gender_flagged.length} flagged — gender unknown
                </span>
                <button
                  type="button"
                  onClick={() => setImportSummary(null)}
                  data-testid="import-summary-dismiss"
                  className="ml-auto text-[10px] uppercase tracking-widest text-[#525252] hover:text-white"
                >
                  Dismiss
                </button>
              </div>
              {importSummary.gender_flagged.length > 0 && (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFlaggedSelected(new Set(importSummary.gender_flagged.map((p) => p.id)))}
                      data-testid="flagged-select-all"
                      className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setFlaggedSelected(new Set())}
                      data-testid="flagged-select-none"
                      className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white"
                    >
                      Clear
                    </button>
                    <span className="text-[10px] text-[#525252]">·</span>
                    <button
                      type="button"
                      onClick={() => bulkGender(Array.from(flaggedSelected), "male")}
                      disabled={bulkBusy || flaggedSelected.size === 0}
                      data-testid="flagged-bulk-male"
                      className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40"
                    >
                      <Mars className="w-3 h-3" /> Assign Male ({flaggedSelected.size})
                    </button>
                    <button
                      type="button"
                      onClick={() => bulkGender(Array.from(flaggedSelected), "female")}
                      disabled={bulkBusy || flaggedSelected.size === 0}
                      data-testid="flagged-bulk-female"
                      className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#FF66C4] text-[#FF66C4] hover:bg-[#FF66C4] hover:text-black disabled:opacity-40"
                    >
                      <Venus className="w-3 h-3" /> Assign Female ({flaggedSelected.size})
                    </button>
                  </div>
                  <div className="mt-3 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1" data-testid="flagged-players-list">
                    {importSummary.gender_flagged.map((fp) => {
                      const on = flaggedSelected.has(fp.id);
                      return (
                        <label
                          key={fp.id}
                          data-testid={`flagged-row-${fp.id}`}
                          className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer border ${on ? "border-[#CCFF00] bg-[#0F0F0A]" : "border-[#262626] hover:bg-[#0F0F0F]"}`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => {
                              setFlaggedSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(fp.id)) next.delete(fp.id); else next.add(fp.id);
                                return next;
                              });
                            }}
                            className="w-3 h-3 accent-[#CCFF00]"
                          />
                          <span className="truncate">{fp.first_name} {fp.last_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
          {/* Iter205/234 — bulk-action bar hidden for helpers.
              Iter461 — Also hidden when the festival has ended for
              non-admin editors so there's no mutation surface left. */}
          {!isHelper && !isReadOnly && selected.size > 0 && (
            <div className="flex items-center justify-between p-2 border-b border-[#262626] bg-[#141414]" data-testid="players-bulk-bar">
              <span className="text-[11px] uppercase tracking-wider text-[#CCFF00]">
                {selected.size} selected
                {(() => {
                  const awaiting = players.filter((p) => selected.has(p.id) && p.status === "imported").length;
                  return awaiting > 0
                    ? <span className="ml-2 text-[#FFB020]">· {awaiting} awaiting check-in</span>
                    : null;
                })()}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  data-testid="players-bulk-clear"
                  className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white"
                >
                  Clear selection
                </button>
                {/* Iter206 — Bulk check-in. */}
                <button
                  type="button"
                  onClick={checkInSelected}
                  disabled={bulkBusy || players.filter((p) => selected.has(p.id) && p.status === "imported").length === 0}
                  data-testid="players-bulk-checkin"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Advance every selected 'Awaiting Check-In' row to Checked-In"
                >
                  <CheckCircle2 className="w-3 h-3" /> Check-in selected
                </button>
                {/* Iter381 · Fix 3 — Bulk status buttons. */}
                <button
                  type="button"
                  onClick={() => bulkStatus("resting", "Resting")}
                  disabled={bulkBusy}
                  data-testid="players-bulk-resting"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#A3A3A3] text-[#A3A3A3] hover:bg-[#A3A3A3] hover:text-black disabled:opacity-40"
                  title="Mark every selected player as Resting"
                >
                  Rest
                </button>
                <button
                  type="button"
                  onClick={() => bulkStatus("available", "Available")}
                  disabled={bulkBusy}
                  data-testid="players-bulk-available"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-40"
                  title="Mark every selected player as Available"
                >
                  Available
                </button>
                <button
                  type="button"
                  onClick={() => bulkStatus("sin_bin", "Sin-Bin")}
                  disabled={bulkBusy}
                  data-testid="players-bulk-sinbin"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black disabled:opacity-40"
                  title="Send every selected player to the Sin-Bin"
                >
                  Sin-Bin
                </button>
                <button
                  type="button"
                  onClick={() => bulkStatus("done_for_day", "Done for the Day")}
                  disabled={bulkBusy}
                  data-testid="players-bulk-done"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#525252] text-[#A3A3A3] hover:bg-[#525252] hover:text-white disabled:opacity-40"
                  title="Mark every selected player Done for the day"
                >
                  Done
                </button>
                <button
                  type="button"
                  onClick={() => bulkGender(Array.from(selected), "male")}
                  disabled={bulkBusy || selected.size === 0}
                  data-testid="players-bulk-male"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40"
                  title="Assign Male to every selected player"
                >
                  <Mars className="w-3 h-3" /> Male
                </button>
                <button
                  type="button"
                  onClick={() => bulkGender(Array.from(selected), "female")}
                  disabled={bulkBusy || selected.size === 0}
                  data-testid="players-bulk-female"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#FF66C4] text-[#FF66C4] hover:bg-[#FF66C4] hover:text-black disabled:opacity-40"
                  title="Assign Female to every selected player"
                >
                  <Venus className="w-3 h-3" /> Female
                </button>
                <button
                  type="button"
                  onClick={deleteSelected}
                  disabled={bulkBusy}
                  data-testid="players-bulk-delete"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-bold border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white disabled:opacity-40"
                >
                  <Trash2 className="w-3 h-3" /> Delete selected
                </button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#525252] border-b border-[#262626]">
                {!isHelper && (
                  <th className="p-3 w-8">
                    <input
                      type="checkbox"
                      data-testid="players-select-all"
                      checked={players.length > 0 && selected.size === players.length}
                      onChange={toggleSelectAll}
                      disabled={isReadOnly}
                      className="w-3.5 h-3.5 accent-[#CCFF00] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th className="text-left p-3">Name</th>
                {!isHelper && <th className="text-left p-3">Booking</th>}
                {!isHelper && <th className="text-left p-3">Rating</th>}
                {!isHelper && <th className="text-left p-3">Band</th>}
                <th className="text-left p-3">Status</th>
                {!isHelper && <th className="text-left p-3">Source</th>}
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Iter230/233 — client-side pipeline: search → sort.
                let arr = [...players];
                const q = searchQ.trim().toLowerCase();
                if (q) {
                  arr = arr.filter((p) => {
                    const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
                    const bk = (p.booking_id || "").toLowerCase();
                    return name.includes(q) || bk.includes(q);
                  });
                }
                if (sortMode === "az") {
                  arr.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
                } else if (sortMode === "za") {
                  arr.sort((a, b) => `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`));
                } else if (sortMode === "booking") {
                  arr.sort((a, b) => (a.booking_id || "zzz").localeCompare(b.booking_id || "zzz"));
                } else if (sortMode === "band") {
                  arr.sort((a, b) => {
                    const ao = BAND_ORDER[a.colour_band ?? "null"] ?? 99;
                    const bo = BAND_ORDER[b.colour_band ?? "null"] ?? 99;
                    if (ao !== bo) return ao - bo;
                    return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
                  });
                } else if (sortMode === "status") {
                  arr.sort((a, b) => {
                    const ao = STATUS_ORDER[a.status] ?? 99;
                    const bo = STATUS_ORDER[b.status] ?? 99;
                    if (ao !== bo) return ao - bo;
                    return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
                  });
                }
                // Iter233 — remember the first row we see for each
                // starting letter (first or last name) so the A-Z jump
                // bar can scroll straight to it.
                rowRefs.current = {};
                const seenLetters = new Set();
                arr.forEach((p) => {
                  const src = jumpBy === "last" ? (p.last_name || "") : (p.first_name || "");
                  const L = (src.trim()[0] || "").toUpperCase();
                  if (L && /[A-Z]/.test(L) && !seenLetters.has(L)) {
                    seenLetters.add(L);
                    p.__jumpLetter = L;
                  } else {
                    p.__jumpLetter = null;
                  }
                });
                if (arr.length === 0 && q) {
                  return (
                    <tr>
                      <td colSpan={isHelper ? 3 : 8} className="p-8 text-center text-sm text-[#525252] italic" data-testid="players-search-empty">
                        No player matches "{searchQ}" — try a different name or booking ID.
                      </td>
                    </tr>
                  );
                }
                return arr.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`player-row-${p.id}`}
                  data-removed={p.removed_from_festival ? "1" : "0"}
                  data-noshow-today={(p.no_show_dates || []).includes(new Date().toISOString().slice(0, 10)) ? "1" : "0"}
                  ref={p.__jumpLetter ? (el) => { if (el) rowRefs.current[p.__jumpLetter] = el; } : undefined}
                  data-jump-letter={p.__jumpLetter || undefined}
                  className={`border-b border-[#141414] hover:bg-[#0F0F0F] ${p.removed_from_festival ? "opacity-40" : (p.no_show_dates || []).includes(new Date().toISOString().slice(0, 10)) ? "opacity-60" : ""} ${!isHelper && selected.has(p.id) ? "bg-[#0F0F0A]" : ""}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      data-testid={`player-select-${p.id}`}
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      disabled={isReadOnly}
                      className="w-3.5 h-3.5 accent-[#CCFF00] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label={`Select ${p.first_name} ${p.last_name}`}
                    />
                  </td>
                  <td className="p-3 font-medium relative">
                    {(() => {
                      // Iter326 · Bug 4 — Player QR hover is strictly
                      // gated behind an active Self-Service add-on so
                      // helpers can't accidentally reveal a QR that
                      // wouldn't work anyway (public self-service is
                      // off for this festival).
                      // Iter367 · Fix 4 — Also expose the QR for
                      // helper entries that carry a `helper_pass_id`
                      // and aren't revoked, so an organiser can
                      // hover a helper's name in the Player Manager
                      // and see their shift-pass QR (active or
                      // pending, never revoked).
                      const helperQrEnabled = qrHoverEnabled
                        && !!p.helper_pass_id
                        && !p.helper_pass_revoked;
                      const playerQrEnabled = qrHoverEnabled
                        && !!p.self_service_token
                        && !!selfServiceOn;
                      const canHover = helperQrEnabled || playerQrEnabled;
                      const qrUrl = helperQrEnabled
                        ? `${window.location.origin}/festival/${id}/helper/${p.helper_pass_id}`
                        : (playerQrEnabled
                          ? `${window.location.origin}/festival/${id}/me/${p.self_service_token}`
                          : "");
                      const qrLabel = helperQrEnabled ? "HELPER SHIFT PASS" : "SELF-SERVICE QR";
                      return (
                        <>
                          <span
                            className={canHover ? "cursor-help border-b border-dashed border-[#CCFF00]" : ""}
                            onMouseEnter={() => {
                              if (!canHover) return;
                              setHoveredQr({
                                id: p.id,
                                name: `${p.first_name} ${p.last_name}`,
                                url: qrUrl,
                                label: qrLabel,
                              });
                            }}
                            onMouseLeave={() => setHoveredQr((cur) => (cur && cur.id === p.id ? null : cur))}
                            data-testid={`player-name-${p.id}`}
                          >
                            {p.first_name} {p.last_name}
                          </span>
                          {/* Iter392 — grey gender-unknown icon. Rendered when
                               the player has no gender set (missing OR
                               imported as blank / invalid AND inference
                               couldn't resolve). Male/female icons are
                               coloured so OPS can eyeball the roster
                               balance at a glance. */}
                          {p.gender === "male" && (
                            <Mars className="inline-block ml-1.5 w-3 h-3 text-[#66C2FF]" data-testid={`player-gender-male-${p.id}`} />
                          )}
                          {p.gender === "female" && (
                            <Venus className="inline-block ml-1.5 w-3 h-3 text-[#FF66C4]" data-testid={`player-gender-female-${p.id}`} />
                          )}
                          {!p.gender && (
                            <HelpCircle
                              className="inline-block ml-1.5 w-3 h-3 text-[#525252]"
                              data-testid={`player-gender-unknown-${p.id}`}
                              aria-label="Gender not set — select this player and use the bulk bar to assign"
                            />
                          )}
                          {canHover && hoveredQr && hoveredQr.id === p.id && (
                            <div
                              className="fixed z-[70] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0A0A0A] border-2 border-[#CCFF00] p-4 shadow-2xl pointer-events-auto"
                              data-testid={`player-qr-hover-${p.id}`}
                              onMouseEnter={() => setHoveredQr(hoveredQr)}
                              onMouseLeave={() => setHoveredQr(null)}
                            >
                              <div className="label-mono text-[#CCFF00] text-[10px] mb-2">{hoveredQr.label || "SELF-SERVICE QR"}</div>
                              <div className="bg-white p-2">
                                <QRCodeSVG value={hoveredQr.url} size={320} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: 240, height: 240, imageRendering: "pixelated" }} />
                              </div>
                              <div className="text-[11px] text-white mt-2 text-center font-medium max-w-[240px] truncate" title={hoveredQr.url}>{hoveredQr.name}</div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="p-3 text-xs text-[#A3A3A3]">{isHelper ? null : (p.booking_id || "—")}</td>
                  {!isHelper && (
                  <td className="p-3">
                    <button type="button" onClick={() => overrideRating(p)} disabled={isReadOnly} data-testid={`player-rating-${p.id}`} title={isReadOnly ? "Festival ended — read-only" : undefined} className="hover:text-[#CCFF00] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-current">
                      {p.current_rating ?? "—"}
                    </button>
                  </td>
                  )}
                  {!isHelper && (
                  <td className="p-3">
                    {(() => {
                      const st = bandStyleFor(bandStyles, p.colour_band);
                      return (
                        <span
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border inline-block"
                          style={{ backgroundColor: st.bg, color: st.fg, borderColor: st.border }}
                        >
                          {p.colour_band || "unrated"}
                        </span>
                      );
                    })()}
                  </td>
                  )}
                  <td className="p-3 text-xs">
                    {p.done_for_now && (
                      <span
                        data-testid={`player-done-for-day-${p.id}`}
                        className="inline-block mr-2 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#FF4500] bg-[#4B0F0F] text-[#FF6B4B] font-bold"
                        title="Player has confirmed Done for today via self-service. Change their status here to reactivate."
                      >
                        Done for day
                      </span>
                    )}
                    {isHelper ? (
                      <span
                        data-testid={`player-status-label-${p.id}`}
                        className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${p.status === "imported" ? "border-[#FFB020] text-[#FFB020]" : "border-[#262626] text-[#A3A3A3]"}`}
                      >
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    ) : (
                      <select
                        value={p.status}
                        onChange={(e) => saveEdit(p.id, { status: e.target.value })}
                        disabled={isReadOnly}
                        data-testid={`player-status-${p.id}`}
                        title={isReadOnly ? "Festival ended — read-only" : undefined}
                        className="bg-transparent border border-[#262626] px-2 py-1 focus:outline-none focus:border-[#CCFF00] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    )}
                  </td>
                  {!isHelper && <td className="p-3 text-xs text-[#525252] uppercase">{p.source}</td>}
                  <td className="p-3 text-right">
                    {isHelper ? (
                      p.status === "imported" ? (
                        <button
                          type="button"
                          onClick={() => quickCheckIn(p)}
                          data-testid={`player-checkin-${p.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                          title="Advance this player to Checked-In"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Check-in
                        </button>
                      ) : (
                        <span
                          data-testid={`player-checkedin-${p.id}`}
                          className="text-[10px] uppercase tracking-wider text-[#525252] inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3 text-[#4CAF50]" /> Done
                        </span>
                      )
                    ) : (<>
                    {selfServiceOn && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!p.self_service_token) { toast.info("Refresh — link generating"); return; }
                          const url = `${window.location.origin}/festival/${id}/me/${p.self_service_token}`;
                          navigator.clipboard?.writeText(url).then(
                            () => toast.success("Self-service link copied"),
                            () => window.prompt("Copy this link", url),
                          );
                        }}
                        data-testid={`player-copy-link-${p.id}`}
                        className="mr-2 text-[#A3A3A3] hover:text-[#CCFF00]"
                        title="Copy player's self-service link"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button type="button" onClick={() => setEditing(p)} disabled={isReadOnly} data-testid={`player-edit-${p.id}`} title={isReadOnly ? "Festival ended — read-only" : undefined} className="text-[#A3A3A3] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#A3A3A3]">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setNoShowTarget(p)}
                      disabled={isReadOnly}
                      data-testid={`player-noshow-today-${p.id}`}
                      className="ml-2 text-[#A3A3A3] hover:text-[#FFB020] text-[10px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#A3A3A3]"
                      title={isReadOnly ? "Festival ended — read-only" : "Mark no-show for today only — resets at midnight"}
                    >
                      No show
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRemoveConfirmText(""); setRemoveTarget(p); }}
                      disabled={isReadOnly}
                      data-testid={`player-remove-festival-${p.id}`}
                      className="ml-2 text-[#A3A3A3] hover:text-[#FF4500] text-[10px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#A3A3A3]"
                      title={isReadOnly ? "Festival ended — read-only" : "Permanently remove from festival — cannot be undone"}
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmFor(p)}
                      disabled={isReadOnly}
                      data-testid={`player-delete-${p.id}`}
                      className="ml-2 text-[#A3A3A3] hover:text-[#FF4500] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#A3A3A3]"
                      title={isReadOnly ? "Festival ended — read-only" : "Delete player"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    </>)}
                  </td>
                </tr>
                ));
              })()}
            </tbody>
          </table>
          {/* Iter233 — A-Z jump bar (fixed to the right, scrolls to
              the first row starting with the chosen letter). Hidden
              on small screens where the roster wraps below the bar
              width and the search box already provides quick access. */}
          <div
            className="hidden lg:flex fixed right-2 top-1/2 -translate-y-1/2 z-30 flex-col items-center gap-0.5 bg-[#0A0A0A]/95 border border-[#262626] px-1 py-2"
            data-testid="players-jump-bar"
          >
            <div className="flex flex-col gap-0.5 mb-2" role="group" aria-label="Jump by">
              <button
                type="button"
                onClick={() => changeJumpBy("first")}
                data-testid="players-jump-by-first"
                className={`text-[8px] uppercase tracking-widest px-1 py-0.5 ${jumpBy === "first" ? "text-[#CCFF00] border border-[#CCFF00]" : "text-[#525252] border border-transparent hover:text-white"}`}
                title="Alphabet targets the FIRST name"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => changeJumpBy("last")}
                data-testid="players-jump-by-last"
                className={`text-[8px] uppercase tracking-widest px-1 py-0.5 ${jumpBy === "last" ? "text-[#CCFF00] border border-[#CCFF00]" : "text-[#525252] border border-transparent hover:text-white"}`}
                title="Alphabet targets the LAST name"
              >
                Last
              </button>
            </div>
            {ALPHABET.map((L) => {
              const target = rowRefs.current[L];
              const enabled = !!target;
              return (
                <button
                  key={L}
                  type="button"
                  disabled={!enabled}
                  onClick={() => {
                    if (target && target.scrollIntoView) {
                      target.scrollIntoView({ behavior: "smooth", block: "center" });
                      target.classList.add("ring-2", "ring-[#CCFF00]");
                      setTimeout(() => target.classList.remove("ring-2", "ring-[#CCFF00]"), 1400);
                    }
                  }}
                  data-testid={`players-jump-${L}`}
                  data-enabled={enabled ? "1" : "0"}
                  className={`text-[10px] leading-none font-mono px-1 py-0.5 ${enabled ? "text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black" : "text-[#262626] cursor-not-allowed"}`}
                  aria-label={`Jump to ${L}`}
                >
                  {L}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Iter235b — Quick helper pass modal (owner + festival_only_helper). */}
      <HelperPassQuickModal
        festivalId={id}
        open={showHelperQuick}
        onClose={() => setShowHelperQuick(false)}
        licenseActive={["active_paid", "complimentary", "override"].includes(festival?.license?.state)}
      />

      {/* Iter318/322 — NO SHOW TODAY + REMOVE modals (extracted to
          FestivalPlayersModals.jsx to keep this page under 1,200 lines). */}
      <NoShowTodayModal
        open={!!noShowTarget}
        target={noShowTarget}
        busy={rowActionBusy}
        onCancel={() => setNoShowTarget(null)}
        onConfirm={doNoShowToday}
      />
      <RemoveFromFestivalModal
        open={!!removeTarget}
        target={removeTarget}
        busy={rowActionBusy}
        confirmText={removeConfirmText}
        setConfirmText={setRemoveConfirmText}
        onCancel={() => { setRemoveTarget(null); setRemoveConfirmText(""); }}
        onConfirm={doRemoveFromFestival}
      />

      {/* Iter331 · Bug 12 — Per-player DELETE confirmation. Warns the
          organiser that the imported cap SLOT is not refunded on
          delete (cap high-water mark persists per iter318). */}
      {deleteConfirmFor && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/80 p-4" data-testid="delete-player-modal">
          <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FF4500] p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <AlertOctagon className="w-6 h-6 text-[#FF4500] shrink-0 mt-0.5" />
              <div>
                <div className="label-mono text-[#FF4500] mb-1">DELETE PLAYER</div>
                <h2 className="h-display text-xl leading-tight">
                  Remove {deleteConfirmFor.first_name} {deleteConfirmFor.last_name}?
                </h2>
                <p className="text-sm text-[#E5E5E5] mt-2">
                  This deletes them from every roster, court and history for this festival.
                </p>
                <p className="text-xs text-[#FFB020] mt-3 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    The <b>imported player slot is NOT refunded</b>. Your tier's cap high-water
                    mark stays where it is — adding a replacement later will still count against
                    the same cap.
                  </span>
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setDeleteConfirmFor(null)}
                data-testid="delete-player-cancel"
                className="px-3 py-1.5 text-xs border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const target = deleteConfirmFor;
                  setDeleteConfirmFor(null);
                  try {
                    await api.delete(`/festival/${id}/players/${target.id}`);
                    toast.success("Player deleted");
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
                  }
                }}
                data-testid="delete-player-confirm"
                className="px-3 py-1.5 text-xs bg-[#FF4500] text-white hover:bg-[#E63E00]"
              >
                Delete player
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter234 — Clear-All type-to-confirm modal. Wired to the
          "Clear All" toolbar button. Requires the organiser to type
          the festival's exact name so the ✕ next to the search box
          can never trigger a full wipe by accident. */}
      {showClearModal && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/80 p-4" data-testid="clear-all-modal">
          <div className="w-full max-w-lg bg-[#0A0A0A] border-2 border-[#FF4500] p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertOctagon className="w-8 h-8 text-[#FF4500] shrink-0 mt-0.5" />
              <div>
                <div className="label-mono text-[#FF4500] mb-1">DANGER · DESTRUCTIVE ACTION</div>
                <h2 className="h-display text-2xl leading-tight">Are you sure?</h2>
                <p className="text-sm text-[#E5E5E5] mt-2">
                  This will <span className="text-[#FF4500] font-bold">permanently delete every player</span> ({summary.total || 0}) from <span className="text-white font-bold">{festival?.name}</span> and clear every court on this festival. This action runs immediately — an Undo toast is offered for <span className="text-[#FFB020]">10 seconds only</span>.
                </p>
                <p className="text-xs text-[#FFB020] mt-3 flex items-start gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>If you meant to clear the search box, tap the ✕ next to the search input instead — that just resets your filter.</span>
                </p>
              </div>
            </div>
            <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1" htmlFor="clear-confirm-input">
              To confirm, type the festival name: <span className="text-white">{festival?.name}</span>
            </label>
            <input
              id="clear-confirm-input"
              type="text"
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              data-testid="clear-all-confirm-input"
              autoFocus
              placeholder={festival?.name || ""}
              className="w-full text-sm bg-[#141414] border border-[#262626] focus:border-[#FF4500] focus:outline-none px-3 py-2 mb-4 text-white"
            />
            <div className="flex justify-end gap-2 pt-3 border-t border-[#262626]">
              <button
                type="button"
                onClick={() => { setShowClearModal(false); setClearConfirmText(""); }}
                data-testid="clear-all-cancel"
                disabled={clearBusy}
                className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doClearAll}
                data-testid="clear-all-confirm"
                disabled={clearBusy || clearConfirmText.trim().toLowerCase() !== (festival?.name || "").trim().toLowerCase()}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Eraser className="w-4 h-4" /> {clearBusy ? "Wiping…" : `Delete all ${summary.total || 0} players`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import preview modal */}
      {importPreview && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="import-preview-modal">
          <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#262626] p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="label-mono text-[#CCFF00] mb-1">IMPORT PREVIEW</div>
                <h2 className="h-display text-xl">Ready to import {importPreview.total_rows} players?</h2>
              </div>
              <button type="button" onClick={() => setImportPreview(null)} data-testid="import-preview-close" className="text-[#A3A3A3] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {Object.entries(importPreview.band_counts).map(([b, n]) => {
                const st = bandStyleFor(bandStyles, b);
                return (
                  <div
                    key={b}
                    className="p-2 border text-center"
                    style={{ backgroundColor: st.bg, color: st.fg, borderColor: st.border }}
                  >
                    <div className="text-[10px] uppercase tracking-wider">{b}</div>
                    <div className="text-lg font-bold">{n}</div>
                  </div>
                );
              })}
            </div>
            {importPreview.warnings.length > 0 && (
              <div className="border border-[#FFB020] p-3 mb-3" data-testid="import-warnings">
                <div className="flex items-center gap-2 text-[#FFB020] text-xs uppercase tracking-wider mb-2">
                  <AlertTriangle className="w-4 h-4" /> {importPreview.warnings.length} warnings
                </div>
                <ul className="text-[11px] text-[#E5E5E5] space-y-1 max-h-32 overflow-y-auto list-disc pl-4">
                  {importPreview.warnings.slice(0, 30).map((w, i) => (
                    <li key={i}>Row {w.row}: {w.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="pt-3 border-t border-[#262626] flex justify-end gap-2">
              <button type="button" onClick={() => setImportPreview(null)} className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">
                Cancel
              </button>
              <button
                type="button"
                onClick={doImport}
                disabled={importBusy}
                data-testid="import-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
                style={{ background: "#CCFF00", color: "#0A0A0A" }}
              >
                <CheckCircle2 className="w-4 h-4" /> {importBusy ? "Importing…" : `Import ${importPreview.total_rows}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManual && (
        <ManualAddModal onClose={() => setShowManual(false)} onSave={saveManual} />
      )}
      {editing && (
        <EditPlayerModal
          player={editing}
          festivalId={id}
          rosterPlayers={players}
          onClose={() => setEditing(null)}
          onSave={(patch) => saveEdit(editing.id, patch)}
        />
      )}
    </div>
  );
}

function ManualAddModal({ onClose, onSave }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", rating: "", booking_id: "", source: "manual", notes: "", gender: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="manual-add-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0A0A0A] border border-[#262626] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="label-mono text-[#CCFF00] mb-1">MANUAL ADD</div>
        <h2 className="h-display text-xl mb-4">New player</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="First name" data-testid="manual-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
            <input placeholder="Last name" data-testid="manual-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          </div>
          <input placeholder="SAR rating (e.g. 3.5)" type="number" step="0.5" min="0" max="8" data-testid="manual-rating" value={form.rating} onChange={(e) => set("rating", e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          <input placeholder="Booking ID (optional)" data-testid="manual-booking-id" value={form.booking_id} onChange={(e) => set("booking_id", e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          <select value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="manual-source" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]">
            <option value="manual">Late walk-in</option>
            <option value="staff">Helper / staff</option>
            <option value="guest">Guest / spare</option>
          </select>
          <select value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="manual-gender" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]">
            <option value="">Gender — not specified</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <textarea rows={2} placeholder="Notes (optional)" data-testid="manual-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
        </div>
        <div className="pt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
          <button type="button" onClick={() => onSave(form)} data-testid="manual-save" disabled={!form.first_name.trim()} className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
            <Save className="w-4 h-4" /> Add player
          </button>
        </div>
      </div>
    </div>
  );
}

function EditPlayerModal({ player, festivalId, rosterPlayers = [], onClose, onSave }) {
  const [form, setForm] = useState({
    first_name: player.first_name || "",
    last_name: player.last_name || "",
    rating: player.current_rating != null ? String(player.current_rating) : "",
    status: player.status || "arrived",
    notes: player.notes || "",
    booking_id: player.booking_id || "",
    gender: player.gender || "",
    email: player.email || "",
  });
  // Iter395 follow-up — Blocked players list, hydrated by the modal
  // itself so opening the pencil icon shows the current state.
  const [blocks, setBlocks] = useState([]);
  const [blockSearch, setBlockSearch] = useState("");
  const [blockBusy, setBlockBusy] = useState(false);
  const isLocked = !!player.verify_locked;
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get(`/festival/${festivalId}/players/${player.id}/blocks`);
        if (!ignore) setBlocks(r.data?.items || []);
      } catch {
        if (!ignore) setBlocks([]);
      }
    })();
    return () => { ignore = true; };
  }, [festivalId, player.id]);
  const addBlock = async (otherId) => {
    if (!otherId || otherId === player.id) return;
    setBlockBusy(true);
    try {
      const r = await api.post(`/festival/${festivalId}/players/${player.id}/blocks`, { blocked_id: otherId });
      setBlocks(r.data?.items || []);
      setBlockSearch("");
      toast.success("Block added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add block");
    } finally { setBlockBusy(false); }
  };
  const removeBlock = async (otherId) => {
    setBlockBusy(true);
    try {
      const r = await api.delete(`/festival/${festivalId}/players/${player.id}/blocks/${otherId}`);
      setBlocks(r.data?.items || []);
      toast.success("Block removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove block");
    } finally { setBlockBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    const patch = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      status: form.status,
      notes: form.notes.trim() || null,
      booking_id: form.booking_id.trim() || "",
      gender: form.gender || null,
    };
    if (form.email.trim() !== (player.email || "")) patch.email = form.email.trim();
    if (form.rating !== "" && !Number.isNaN(Number(form.rating))) {
      patch.current_rating = Number(form.rating);
    }
    onSave(patch);
  };
  const resetLock = () => onSave({ reset_verify_lock: true });
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="edit-player-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0A0A0A] border border-[#262626] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="label-mono text-[#CCFF00] mb-1">EDIT PLAYER</div>
        <h2 className="h-display text-xl mb-4">{player.first_name} {player.last_name}</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="edit-first-name" placeholder="First name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
            <input data-testid="edit-last-name" placeholder="Last name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#525252] block mb-1">Email</label>
            <input data-testid="edit-email" type="email" placeholder="player@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#525252] block mb-1">Operational rating (0–8)</label>
            <input
              data-testid="edit-rating"
              type="number"
              step="0.1"
              min="0"
              max="8"
              placeholder="e.g. 3.5"
              value={form.rating}
              onChange={(e) => set("rating", e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
            />
            <div className="text-[9px] text-[#525252] mt-1">Colour band updates automatically. Change logs to the player's rating history.</div>
          </div>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="edit-status" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]">
            {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#525252] block mb-1">Gender</label>
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="edit-gender" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]">
              <option value="">Not specified</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#525252] block mb-1">Booking ID · self-service code</label>
            <input
              data-testid="edit-booking-id"
              placeholder="e.g. 1234Fest"
              value={form.booking_id}
              onChange={(e) => set("booking_id", e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
            />
            <div className="text-[9px] text-[#525252] mt-1">
              Players type this after scanning their QR. Share it verbally with walk-ins.
              {isLocked && (
                <span className="ml-2 text-[#FF4500]">Locked after too many wrong tries.</span>
              )}
            </div>
            {isLocked && (
              <button
                type="button"
                onClick={resetLock}
                data-testid="edit-reset-lock"
                className="mt-2 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white"
              >
                Reset lock
              </button>
            )}
          </div>
          <textarea rows={2} placeholder="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]" />
          {/* Iter395 follow-up — Blocked players section. OPS search
               a roster player by name, add them; each existing block
               shows the other player's name + a remove pill. Backend
               enforces the mutual invariant. */}
          <div className="pt-3 border-t border-[#1f1f1f]" data-testid="edit-blocks-section">
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
              Blocked players
              <span className="ml-2 normal-case tracking-normal text-[#525252]">
                — {blocks.length} on this list
              </span>
            </label>
            <input
              type="text"
              placeholder="Search a player to block…"
              value={blockSearch}
              onChange={(e) => setBlockSearch(e.target.value)}
              data-testid="edit-blocks-search"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00] mb-2"
            />
            {blockSearch.trim() && (() => {
              const q = blockSearch.trim().toLowerCase();
              const blockedIds = new Set(blocks.map((b) => b.id));
              const candidates = rosterPlayers
                .filter((rp) =>
                  rp.id !== player.id
                  && !blockedIds.has(rp.id)
                  && `${rp.first_name || ""} ${rp.last_name || ""}`.toLowerCase().includes(q),
                )
                .slice(0, 8);
              if (candidates.length === 0) {
                return (
                  <div className="text-[10px] text-[#525252] italic mb-2" data-testid="edit-blocks-no-match">
                    No matching player on this festival.
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 gap-1 mb-2 max-h-40 overflow-y-auto" data-testid="edit-blocks-suggestions">
                  {candidates.map((rp) => (
                    <button
                      type="button"
                      key={rp.id}
                      onClick={() => addBlock(rp.id)}
                      disabled={blockBusy}
                      data-testid={`edit-blocks-add-${rp.id}`}
                      className="text-left px-2 py-1 text-xs border border-[#262626] hover:border-[#CCFF00] hover:bg-[#0F0F0A] disabled:opacity-40 flex items-center justify-between"
                    >
                      <span>{rp.first_name} {rp.last_name}</span>
                      <span className="text-[9px] uppercase tracking-widest text-[#CCFF00]">+ Block</span>
                    </button>
                  ))}
                </div>
              );
            })()}
            {blocks.length > 0 ? (
              <div className="flex flex-wrap gap-1" data-testid="edit-blocks-list">
                {blocks.map((b) => (
                  <span
                    key={b.id}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] border border-[#FF4500] text-[#FF4500]"
                    data-testid={`edit-blocks-chip-${b.id}`}
                  >
                    {b.first_name} {b.last_name}
                    <button
                      type="button"
                      onClick={() => removeBlock(b.id)}
                      disabled={blockBusy}
                      data-testid={`edit-blocks-remove-${b.id}`}
                      className="ml-1 hover:text-white disabled:opacity-40"
                      title="Remove this block"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-[10px] text-[#525252] italic" data-testid="edit-blocks-empty">
                No blocks yet — search above to add.
              </div>
            )}
          </div>
        </div>
        <div className="pt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
          <button type="button" onClick={submit} data-testid="edit-save" disabled={!form.first_name.trim()} className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
